/// <reference types="google.maps" />
import {
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  InjectionToken,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { LoggingService } from '../services/logging.service';

export const GOOGLE_MAPS_API_KEY_FN = new InjectionToken<(partnerId: string) => Promise<string | null>>(
  'GOOGLE_MAPS_API_KEY_FN'
);

export interface ParsedAddress {
  street: string;
  unit: string;
  city: string;
  state: string;
  zipCode: string;
  fullAddress: string;
}

@Directive({
  selector: '[ldGooglePlacesAutocomplete]',
})
export class GooglePlacesAutocompleteDirective implements OnInit, OnDestroy {
  private readonly elementRef = inject(ElementRef);
  private readonly ngZone = inject(NgZone);
  private readonly getGoogleMapsApiKey = inject(GOOGLE_MAPS_API_KEY_FN, { optional: true });
  private readonly loggingService = inject(LoggingService);

  @Input() partnerId = 'loandepot';

  @Output() addressSelected = new EventEmitter<ParsedAddress>();

  private autocomplete: google.maps.places.Autocomplete | null = null;
  private listener: google.maps.MapsEventListener | null = null;

  async ngOnInit(): Promise<void> {
    try {
      await this.loadGoogleMapsScript();
      this.initAutocomplete();
    } catch (error) {
      this.loggingService.logException(error, 'GooglePlacesAutocomplete: Address autocomplete unavailable');
    }
  }

  ngOnDestroy(): void {
    if (this.listener) {
      google.maps.event.removeListener(this.listener);
    }
  }

  private async loadGoogleMapsScript(): Promise<void> {
    if (typeof google !== 'undefined' && google.maps?.places) {
      return;
    }

    const apiKey = await this.getGoogleMapsApiKey?.(this.partnerId);
    if (!apiKey) {
      throw new Error('Failed to get Google Maps API key');
    }

    return new Promise((resolve, reject) => {
      if (typeof google !== 'undefined' && google.maps?.places) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Maps script'));
      document.head.appendChild(script);
    });
  }

  private initAutocomplete(): void {
    if (typeof google === 'undefined' || !google.maps?.places) {
      return;
    }

    this.autocomplete = new google.maps.places.Autocomplete(this.elementRef.nativeElement, {
      types: ['address'],
      componentRestrictions: { country: 'us' },
    });

    this.listener = this.autocomplete.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = this.autocomplete?.getPlace();
        if (place?.address_components) {
          const parsed = this.parseAddressComponents(place.address_components, place.formatted_address || '');
          this.addressSelected.emit(parsed);
        }
      });
    });
  }

  private parseAddressComponents(
    components: google.maps.GeocoderAddressComponent[],
    formattedAddress: string
  ): ParsedAddress {
    const getComponent = (type: string): string => {
      const component = components.find((c) => c.types.includes(type));
      return component?.short_name || '';
    };

    const getLongComponent = (type: string): string => {
      const component = components.find((c) => c.types.includes(type));
      return component?.long_name || '';
    };

    const streetNumber = getComponent('street_number');
    const route = getLongComponent('route');
    const unit = getComponent('subpremise');
    const city =
      getLongComponent('locality') ||
      getLongComponent('sublocality_level_1') ||
      getLongComponent('neighborhood') ||
      getLongComponent('administrative_area_level_3') ||
      getLongComponent('administrative_area_level_2');
    const state = getComponent('administrative_area_level_1');
    const zipCode = getComponent('postal_code');

    return {
      street: streetNumber ? `${streetNumber} ${route}` : route,
      unit,
      city,
      state,
      zipCode,
      fullAddress: formattedAddress,
    };
  }
}
